import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowDown } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { AWWatermark } from "../components/AWWatermark";
import { buildWhatsAppLink, LOGO_URL } from "../constants/site";
import { useLanguage } from "../i18n/LanguageContext";
import { usePageMeta } from "../hooks/usePageMeta";

const Home = () => {
  const { t, images } = useLanguage();
  usePageMeta(t.meta.home);

  return (
    <div data-testid="home-page" className="bg-white">
      {/* HERO */}
      <section data-testid="hero-section" className="relative h-screen min-h-[680px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={images.hero}
            alt="Luxusuhr – Alpha Watch Zürich"
            fetchPriority="high"
            className="w-full h-full object-cover aw-grayscale"
          />
          <div className="absolute inset-0 bg-black/50" />
        </div>

        <div className="relative z-10 text-center px-6 max-w-5xl">
          <Reveal delay={0.2}>
            <p className="font-sans-aw text-[11px] tracking-luxury uppercase text-white/70 mb-8">
              {t.hero.eyebrow}
            </p>
          </Reveal>
          <Reveal delay={0.4}>
            <h1 className="font-serif-aw text-white text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-light leading-[1.05] tracking-tight">
              {t.hero.title_line1}
              <span className="block italic font-light text-white/95">{t.hero.title_line2}</span>
            </h1>
          </Reveal>
          <Reveal delay={0.7}>
            <p className="font-sans-aw text-white/80 text-base md:text-lg mt-10 max-w-2xl mx-auto leading-relaxed">
              {t.hero.subtitle}
            </p>
          </Reveal>
          <Reveal delay={0.9}>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mt-12">
              <a
                data-testid="hero-cta-whatsapp"
                href={buildWhatsAppLink(t.whatsapp.default_message)}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-aw btn-aw-light font-sans-aw text-[11px] tracking-luxury uppercase px-8 py-4 inline-flex items-center justify-center gap-3"
              >
                {t.hero.cta_primary}
                <ArrowRight size={14} strokeWidth={1.5} />
              </a>
              <Link
                data-testid="hero-cta-services"
                to="/services"
                className="font-sans-aw text-[11px] tracking-luxury uppercase px-8 py-4 inline-flex items-center justify-center gap-3 text-white border border-white/40 hover:border-white transition-colors duration-500"
              >
                {t.hero.cta_secondary}
                <ArrowRight size={14} strokeWidth={1.5} />
              </Link>
            </div>
          </Reveal>
        </div>

        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center text-white/60 animate-pulse">
          <span className="font-sans-aw text-[10px] tracking-luxury uppercase mb-3">Scroll</span>
          <ArrowDown size={16} strokeWidth={1} />
        </div>
      </section>

      {/* INTRO */}
      <section data-testid="intro-section" className="relative py-28 md:py-40 px-6 md:px-12 overflow-hidden">
        <AWWatermark size={620} opacity={0.04} position="right" />
        <div className="relative max-w-[1400px] mx-auto grid md:grid-cols-12 gap-10 md:gap-20 items-start">
          <div className="md:col-span-4">
            <Reveal>
              <p className="font-sans-aw text-[10px] tracking-luxury uppercase text-black/50 mb-6">
                — {t.intro.eyebrow}
              </p>
            </Reveal>
          </div>
          <div className="md:col-span-8">
            <Reveal delay={0.1}>
              <h2 className="font-serif-aw text-3xl md:text-5xl lg:text-6xl font-light leading-[1.1] tracking-tight text-black">
                {t.intro.heading}
              </h2>
            </Reveal>
            <Reveal delay={0.25}>
              <p className="font-sans-aw text-base md:text-lg text-black/70 leading-relaxed mt-10 max-w-2xl">
                {t.intro.body}
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* SERVICES TEASER */}
      <section data-testid="services-teaser-section" className="bg-[#fafafa] py-28 md:py-40 px-6 md:px-12">
        <div className="max-w-[1400px] mx-auto">
          <Reveal>
            <p className="font-sans-aw text-[10px] tracking-luxury uppercase text-black/50 mb-4 text-center">
              — {t.services_teaser.eyebrow}
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-serif-aw text-4xl md:text-5xl lg:text-6xl font-light text-center tracking-tight mb-20 md:mb-28">
              {t.services_teaser.heading}
            </h2>
          </Reveal>

          <div className="grid md:grid-cols-2 gap-10 md:gap-16">
            {/* Watches */}
            <Reveal delay={0.2}>
              <div data-testid="teaser-watches" className="group">
                <div className="overflow-hidden mb-8">
                  <img
                    src={images.watch_service}
                    loading="lazy"
                    alt="Luxury watches"
                    className="w-full h-[440px] md:h-[520px] object-cover aw-grayscale gallery-img"
                  />
                </div>
                <p className="font-sans-aw text-[10px] tracking-luxury uppercase text-black/40 mb-3">01 — Métier</p>
                <h3 className="font-serif-aw text-3xl md:text-4xl font-light mb-5">
                  {t.services_teaser.watches_title}
                </h3>
                <p className="font-sans-aw text-base text-black/70 leading-relaxed mb-8 max-w-md">
                  {t.services_teaser.watches_desc}
                </p>
                <Link
                  data-testid="teaser-watches-cta"
                  to="/services"
                  className="font-sans-aw text-[11px] tracking-luxury uppercase link-aw inline-flex items-center gap-3"
                >
                  {t.services_teaser.cta}
                  <ArrowRight size={14} strokeWidth={1.5} />
                </Link>
              </div>
            </Reveal>

            {/* Goldsmith */}
            <Reveal delay={0.35}>
              <div data-testid="teaser-goldsmith" className="group md:mt-24">
                <div className="overflow-hidden mb-8">
                  <img
                    src={images.goldsmith}
                    loading="lazy"
                    alt="Goldsmithing"
                    className="w-full h-[440px] md:h-[520px] object-cover aw-grayscale gallery-img"
                  />
                </div>
                <p className="font-sans-aw text-[10px] tracking-luxury uppercase text-black/40 mb-3">02 — Métier</p>
                <h3 className="font-serif-aw text-3xl md:text-4xl font-light mb-5">
                  {t.services_teaser.goldsmith_title}
                </h3>
                <p className="font-sans-aw text-base text-black/70 leading-relaxed mb-8 max-w-md">
                  {t.services_teaser.goldsmith_desc}
                </p>
                <Link
                  data-testid="teaser-goldsmith-cta"
                  to="/services"
                  className="font-sans-aw text-[11px] tracking-luxury uppercase link-aw inline-flex items-center gap-3"
                >
                  {t.services_teaser.cta}
                  <ArrowRight size={14} strokeWidth={1.5} />
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* QUIET CTA BAND */}
      <section data-testid="cta-band" className="relative py-28 md:py-40 px-6 md:px-12 bg-black text-white overflow-hidden">
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage: `url(${LOGO_URL})`,
            backgroundSize: "340px",
            backgroundRepeat: "repeat",
            filter: "invert(1)",
          }}
        />
        <div className="relative max-w-3xl mx-auto text-center">
          <Reveal>
            <p className="font-sans-aw text-[10px] tracking-luxury uppercase text-white/50 mb-6">
              Atelier Alpha Watch · Zürich
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <h2 className="font-serif-aw text-4xl md:text-5xl lg:text-6xl font-light italic leading-tight">
              {t.closing.home_quote}
            </h2>
          </Reveal>
          <Reveal delay={0.3}>
            <a
              data-testid="cta-band-whatsapp"
              href={buildWhatsAppLink(t.whatsapp.default_message)}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-12 inline-flex items-center gap-3 font-sans-aw text-[11px] tracking-luxury uppercase border border-white/40 hover:border-white px-8 py-4 transition-colors duration-500"
            >
              {t.hero.cta_primary}
              <ArrowRight size={14} strokeWidth={1.5} />
            </a>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Home;
